import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createDefaultAttendanceFilters,
  validateAttendanceDateRange,
} from '../../utils/attendanceFilters';

type Props = NativeStackScreenProps<RootStackParamList, 'AttendanceFilter'>;

/**
 * Backend history filters are fromDate, toDate, and optional attendanceMode.
 * Status filters are not mounted on the self-service history API.
 */
export function AttendanceFilterScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const current = route.params.currentFilters;
  const [fromDate, setFromDate] = useState(current.fromDate);
  const [toDate, setToDate] = useState(current.toDate);
  const [attendanceMode, setAttendanceMode] = useState(
    current.attendanceMode ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      help: {
        ...typography.caption,
        color: palette.textMuted,
        marginBottom: spacing.lg,
      },
      error: { ...typography.body, color: palette.error, marginTop: spacing.sm },
    }),
    [palette],
  );

  const apply = () => {
    const rangeError = validateAttendanceDateRange(fromDate.trim(), toDate.trim());
    if (rangeError) {
      setError(rangeError);
      return;
    }
    const mode = attendanceMode.trim();
    navigation.navigate({
      name: routes.attendanceHistory,
      params: {
        appliedFilters: {
          fromDate: fromDate.trim(),
          toDate: toDate.trim(),
          ...(mode ? { attendanceMode: mode } : {}),
        },
      },
      merge: true,
    });
  };

  const reset = () => {
    const defaults = createDefaultAttendanceFilters();
    setFromDate(defaults.fromDate);
    setToDate(defaults.toDate);
    setAttendanceMode('');
    setError(null);
  };

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader
          title="Attendance Filters"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <Text style={themed.help}>
          Select dates from the calendar. Attendance type is optional (Office,
          Manual, or WFH).
        </Text>
        <AppDateField
          label="From date"
          value={fromDate}
          onChange={value => {
            setFromDate(value);
            setError(null);
          }}
        />
        <AppDateField
          label="To date"
          value={toDate}
          onChange={value => {
            setToDate(value);
            setError(null);
          }}
        />
        <AppInput
          label="Attendance mode"
          value={attendanceMode}
          onChangeText={value => {
            setAttendanceMode(value);
            setError(null);
          }}
          placeholder="Optional, e.g. OFFICE or MANUAL"
          autoCapitalize="characters"
        />
        {error ? (
          <Text accessibilityRole="alert" style={themed.error}>
            {error}
          </Text>
        ) : null}
        <View style={styles.actions}>
          <AppButton title="Apply" onPress={apply} />
          <AppButton title="Reset" variant="secondary" onPress={reset} />
          <AppButton
            title="Cancel"
            variant="text"
            onPress={() => navigation.goBack()}
          />
        </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  actions: { gap: spacing.sm, marginTop: spacing.xl },
});
